import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import StatusBadge, { PriorityBadge, TypeBadge } from "@/components/StatusBadge";
import { STATUS_HEX as STATUS_COLOR, STAGE_FLOW } from "@/lib/status";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import { AlertTriangle, Clock, CheckCircle2, TrendingUp, Timer, Hourglass } from "lucide-react";
import { toast } from "sonner";

function Kpi({ label, value, unit, icon: Icon, testid }) {
  return (
    <div className="border border-[#e6e5df] bg-white p-5 rounded-sm relative overflow-hidden" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono">{label}</div>
        <Icon size={16} className="text-[#647265]" strokeWidth={1.6} />
      </div>
      <div className="mt-4 flex items-baseline gap-1.5">
        <div className="num text-4xl font-semibold text-[#232826]">{value}</div>
        {unit && <div className="text-xs text-[#6b6a68] mono uppercase">{unit}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load stats");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) {
    return (
      <div className="p-10 text-[#6b6a68] font-mono text-sm" data-testid="dashboard-loading">Loading metrics…</div>
    );
  }

  const statusData = Object.entries(data.status_counts).map(([name, value]) => ({ name, value })).filter((s) => s.value > 0);
  const stageData = [...STAGE_FLOW, "On Hold"].map((s) => ({ stage: s, hours: data.stage_avg_hours?.[s] ?? 0 }));
  const missingPct = data.total ? Math.round(((data.type_counts?.Missing || 0) / data.total) * 100) : 0;

  return (
    <div>
      <PageHeader
        title="Operations Dashboard"
        subtitle="Real-time view of the rework floor. Ageing alerts, throughput, quality signals."
        testid="dashboard-header"
      />

      <div className="p-6 md:p-10 space-y-8">
        {data.total === 0 && (
          <EmptyState testid="dashboard-empty" title="No rework requests yet"
            body="Metrics, ageing alerts and trends will light up here as soon as the first rework or missing-part request is submitted." />
        )}
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <Kpi label="Open Reworks" value={data.open_total} unit="active" icon={AlertTriangle} testid="kpi-open" />
          <Kpi label="Overdue (>7d stage)" value={data.overdue_count} unit="needs push" icon={Timer} testid="kpi-overdue" />
          <Kpi label="Waiting Pickup" value={data.stale_pending_count} unit="pending >1d" icon={Hourglass} testid="kpi-stale" />
          <Kpi label="Avg Completion" value={data.avg_completion_hours} unit="hrs" icon={Clock} testid="kpi-avg" />
          <Kpi label="Completed / Week" value={data.completed_weekly} unit="last 7d" icon={CheckCircle2} testid="kpi-completed-week" />
          <Kpi label="Completed / Month" value={data.completed_monthly} unit="last 30d" icon={TrendingUp} testid="kpi-completed-month" />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Status pie */}
          <div className="border border-[#e6e5df] bg-white p-5 rounded-sm lg:col-span-1" data-testid="chart-status">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono mb-4">Reworks by Status</div>
            <div className="h-56">
              <ResponsiveContainer minWidth={0} minHeight={1}>
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2} isAnimationActive={false}>
                    {statusData.map((s) => (
                      <Cell key={s.name} fill={STATUS_COLOR[s.name]} stroke="#f7f5f1" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: "#ffffff", border: "1px solid #e6e5df", borderRadius: 2, fontSize: 12, color: "#232826" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {statusData.map((s) => (
                <div key={s.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: STATUS_COLOR[s.name] }} />
                    <span className="text-[#6b6a68]">{s.name}</span>
                  </div>
                  <span className="num text-[#232826]">{s.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trend */}
          <div className="border border-[#e6e5df] bg-white p-5 rounded-sm lg:col-span-2" data-testid="chart-trend">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono mb-4">Monthly Rework Trend</div>
            <div className="h-56">
              <ResponsiveContainer minWidth={0} minHeight={1}>
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e6e5df" />
                  <XAxis dataKey="label" stroke="#a39f98" fontSize={11} fontFamily="IBM Plex Mono" />
                  <YAxis stroke="#a39f98" fontSize={11} fontFamily="IBM Plex Mono" allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: "#ffffff", border: "1px solid #e6e5df", borderRadius: 2, fontSize: 12, color: "#232826" }} />
                  <Line type="monotone" dataKey="count" stroke="#424e43" strokeWidth={2} dot={{ fill: "#424e43", r: 4 }} activeDot={{ r: 6, fill: "#647265" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* By reason + top parts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-[#e6e5df] bg-white p-5 rounded-sm" data-testid="chart-reason">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono mb-4">Reworks by Reason</div>
            <div className="h-64">
              <ResponsiveContainer minWidth={0} minHeight={1}>
                <BarChart data={data.reason_counts} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e6e5df" horizontal={false} />
                  <XAxis type="number" stroke="#a39f98" fontSize={11} fontFamily="IBM Plex Mono" allowDecimals={false} />
                  <YAxis dataKey="reason" type="category" stroke="#6b6a68" fontSize={11} width={130} />
                  <Tooltip contentStyle={{ backgroundColor: "#ffffff", border: "1px solid #e6e5df", borderRadius: 2, fontSize: 12, color: "#232826" }} cursor={{ fill: "#efece5" }} />
                  <Bar dataKey="count" fill="#647265" radius={[0, 0, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-[#e6e5df] bg-white p-5 rounded-sm" data-testid="chart-top-parts">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono mb-4">Top Reworked Parts</div>
            <div className="space-y-3 mt-2">
              {data.top_parts.length === 0 && <div className="text-sm text-[#a39f98]">No data yet.</div>}
              {data.top_parts.map((p, i) => {
                const max = data.top_parts[0].count;
                return (
                  <div key={p.part_number} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="mono text-[#232826]">
                        <span className="text-[#a39f98] mr-2">#{i + 1}</span>{p.part_number}
                      </span>
                      <span className="num text-[#424e43]">{p.count}</span>
                    </div>
                    <div className="h-1.5 bg-[#efece5] rounded-sm overflow-hidden">
                      <div className="h-full bg-[#647265]" style={{ width: `${(p.count / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Stage times + type ratio + top jobs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="border border-[#e6e5df] bg-white p-5 rounded-sm" data-testid="chart-stage-time">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono mb-4">Avg Hours per Stage (bottlenecks)</div>
            <div className="h-56">
              <ResponsiveContainer minWidth={0} minHeight={1}>
                <BarChart data={stageData} margin={{ left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e6e5df" vertical={false} />
                  <XAxis dataKey="stage" stroke="#a39f98" fontSize={10} interval={0} tickFormatter={(s) => s.replace("In Rework ", "")} />
                  <YAxis stroke="#a39f98" fontSize={11} fontFamily="IBM Plex Mono" />
                  <Tooltip contentStyle={{ backgroundColor: "#ffffff", border: "1px solid #e6e5df", borderRadius: 2, fontSize: 12, color: "#232826" }} cursor={{ fill: "#efece5" }} />
                  <Bar dataKey="hours">{stageData.map((s) => <Cell key={s.stage} fill={STATUS_COLOR[s.stage]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-[#e6e5df] bg-white p-5 rounded-sm" data-testid="chart-type-ratio">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono mb-4">Missing vs Rework</div>
            <div className="flex items-end gap-3 mt-6">
              <div className="num text-5xl text-rose-700">{missingPct}%</div>
              <div className="text-xs text-[#6b6a68] pb-2">of requests are missing parts</div>
            </div>
            <div className="h-3 bg-[#efece5] rounded-sm overflow-hidden mt-4 flex">
              <div className="bg-rose-400 h-full" style={{ width: `${missingPct}%` }} />
              <div className="bg-[#647265] h-full flex-1" />
            </div>
            <div className="flex justify-between text-xs mt-2 text-[#6b6a68]">
              <span><TypeBadge type="Missing" /> {data.type_counts?.Missing || 0}</span>
              <span>Rework {data.type_counts?.Rework || 0}</span>
            </div>
            <p className="text-xs text-[#a39f98] mt-4">Missing parts should be found first — a high ratio points at the racks/process, not at the machines.</p>
          </div>

          <div className="border border-[#e6e5df] bg-white p-5 rounded-sm" data-testid="chart-top-jobs">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono mb-4">Jobs with Most Open Reworks</div>
            <div className="space-y-3 mt-2">
              {data.top_jobs.length === 0 && <div className="text-sm text-[#a39f98]">Nothing open.</div>}
              {data.top_jobs.map((j, i) => (
                <div key={j.job_number} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="mono text-[#232826]"><span className="text-[#a39f98] mr-2">#{i + 1}</span>{j.job_number}</span>
                    <span className="num text-[#424e43]">{j.count}</span>
                  </div>
                  <div className="h-1.5 bg-[#efece5] rounded-sm overflow-hidden"><div className="h-full bg-indigo-400" style={{ width: `${(j.count / data.top_jobs[0].count) * 100}%` }} /></div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#a39f98] mt-4">Batch these — optimise all parts for a job together.</p>
          </div>
        </div>

        {/* Open queue */}
        <div className="border border-[#e6e5df] bg-white rounded-sm" data-testid="open-queue">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#e6e5df]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono">Open Rework Queue</div>
            <Link to="/register" className="text-xs text-[#647265] hover:text-[#424e43] hover:underline" data-testid="link-view-all">View all →</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#6b6a68] border-b border-[#e6e5df]">
                  <th className="text-left px-5 py-3 font-medium">Job</th>
                  <th className="text-left py-3 font-medium">Part</th>
                  <th className="text-left py-3 font-medium">Priority</th>
                  <th className="text-left py-3 font-medium">Reason</th>
                  <th className="text-left py-3 font-medium">Assigned</th>
                  <th className="text-left py-3 font-medium">Status</th>
                  <th className="text-right py-3 font-medium">In Stage</th>
                  <th className="text-right px-5 py-3 font-medium">Days Open</th>
                </tr>
              </thead>
              <tbody>
                {data.open_queue.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-10 text-[#a39f98] text-sm">Queue is empty — good job.</td></tr>
                )}
                {data.open_queue.map((r) => {
                  const severe = r.days_open >= 14;
                  const warn = r.days_open >= 7;
                  const stageLate = (r.status === "Pending" && r.stage_days >= 1) || (r.status !== "Pending" && r.status !== "On Hold" && r.stage_days >= 7);
                  return (
                    <tr key={r.id} className="border-b border-[#e6e5df] hover:bg-[#efece5]/60 transition-colors">
                      <td className="px-5 py-3 mono text-[#232826]">
                        <Link to={`/rework/${r.id}`} className="hover:text-[#647265] font-medium" data-testid={`queue-link-${r.id}`}>{r.job_number}</Link>
                        {r.request_type === "Missing" && <span className="ml-2"><TypeBadge type="Missing" /></span>}
                      </td>
                      <td className="py-3 mono text-[#424e43]">{r.part_number}</td>
                      <td className="py-3">{r.priority === "Normal" ? <span className="text-xs text-[#a39f98]">Normal</span> : <PriorityBadge priority={r.priority} />}</td>
                      <td className="py-3 text-[#6b6a68]">{r.rework_reason}</td>
                      <td className="py-3 text-[#6b6a68]">{r.assigned_to || <span className="text-[#a39f98] italic">Unassigned</span>}</td>
                      <td className="py-3"><StatusBadge status={r.status} /></td>
                      <td className={`py-3 text-right num ${stageLate ? "text-[#8a3a2a]" : "text-[#6b6a68]"}`}>{r.stage_days}d</td>
                      <td className={`px-5 py-3 text-right num ${severe ? "text-[#8a3a2a]" : warn ? "text-[#a06b3a]" : "text-[#424e43]"}`}>
                        {r.days_open}d
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
